import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { ffdNotesRoutePrefix, isFfdNotesContext, routes } from '@libs/utils';
import { isMobileOrTablet } from '@libs/ui-lib';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const MobileGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): UrlTree | boolean => {
  const router = inject(Router);
  const platform = inject(Platform);

  const fullPath = route.pathFromRoot.flatMap((r) => r.url.map((u) => u.path)).join('/');
  const isFfdNotes = isFfdNotesContext(fullPath);

  if (!isMobileOrTablet(platform)) {
    return true;
  }

  // Billing tab is reachable on mobile when opened from a billing link (customers receive these
  // by email with ?allowMobile=true). Scoped to the billing route so it unlocks nothing else.
  // Matches both the billing URL itself and the login hop (whose redirectUrl param carries it).
  if (route.queryParams['allowMobile'] === 'true' && state.url.includes(routes.billing)) {
    return true;
  }

  if (isFfdNotes) {
    // Allow ffd-notes login and forgot-password and reset-password routes (they are mobile-optimized)
    const isMobileOptimizedRoute = route.url.some((url) => url.path === routes.forgotPassword || url.path === routes.resetPassword);
    if (isMobileOptimizedRoute) {
      return true;
    }

    return router.createUrlTree([...ffdNotesRoutePrefix(), routes.getTheApp]);
  } else {
    if (route.queryParams['registrationSuccess']) {
      return router.createUrlTree([routes.getTheApp], { queryParams: { registrationSuccess: true } });
    }

    if (route.queryParams['isSiteAdminFlow'] || route.url.some((url) => url.path === routes.siteAdmin)) {
      return router.createUrlTree([routes.getTheApp], { queryParams: { siteAdminOnMobile: true } });
    }

    if (route.queryParams['siteAssocSuccess']) {
      return router.createUrlTree([routes.getTheApp], {
        queryParams: { siteAssocSuccess: true, isPatient: route.queryParams['isPatient'] ? true : undefined },
      });
    }

    // Setup flow is fully accessible on mobile (including its login hop), so let it through here too.
    if (route.queryParams['isSetupFlow'] || route.url.some((url) => url.path === routes.setup)) {
      return true;
    }

    return router.createUrlTree([routes.getTheApp]);
  }
};
